import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArgovLogo } from "@/components/ArgovLogo";
import { ADMIN } from "@/constants/testIds";

const STATUSES = [
  { v: "new", l: "New", c: "#0891b2" },
  { v: "contacted", l: "Contacted", c: "#7c3aed" },
  { v: "qualified", l: "Qualified", c: "#d97706" },
  { v: "won", l: "Won", c: "#16a34a" },
  { v: "lost", l: "Lost", c: "#dc2626" },
];
const SECTORS = [
  { v: "", l: "All sectors" }, { v: "government", l: "Government" }, { v: "bfsi", l: "BFSI" },
  { v: "healthcare", l: "Healthcare" }, { v: "energy", l: "Energy" }, { v: "telecom", l: "Telecom" }, { v: "other", l: "Other" },
];
const PRIORITIES = [{ v: "low", l: "Low" }, { v: "medium", l: "Medium" }, { v: "high", l: "High" }];

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
};

const statusColor = (s) => STATUSES.find((x) => x.v === s)?.c || "#6b7280";

// ---------- Sidebar ----------
const Sidebar = ({ tab, setTab, onLogout, user, stats }) => (
  <aside style={{ width: 260, background: "#0c1a2e", color: "#fff", display: "flex", flexDirection: "column", position: "sticky", top: 0, height: "100vh" }}>
    <div style={{ padding: "22px 24px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
      <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, color: "#fff" }}>
        <ArgovLogo size={30} />
        <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: "-0.02em" }}>ARGOV<span style={{ color: "#0891b2", fontWeight: 500 }}>™</span></span>
      </Link>
      <p className="font-mono" style={{ marginTop: 8, fontSize: 10, letterSpacing: "0.14em", color: "#4a6080", textTransform: "uppercase" }}>CRM · CONTROL PLANE</p>
    </div>

    <nav style={{ padding: 12, display: "flex", flexDirection: "column", gap: 4 }}>
      {[
        { k: "dashboard", l: "Dashboard", ic: "▨", tid: ADMIN.tabDashboard },
        { k: "leads", l: "Leads", ic: "◉", tid: ADMIN.tabLeads },
        { k: "export", l: "Export", ic: "↓", tid: ADMIN.tabExport },
      ].map((t) => (
        <button key={t.k} data-testid={t.tid} onClick={() => setTab(t.k)} style={{
          textAlign: "left", padding: "10px 14px", background: tab === t.k ? "rgba(8,145,178,0.15)" : "transparent",
          border: "1px solid " + (tab === t.k ? "rgba(8,145,178,0.4)" : "transparent"),
          color: tab === t.k ? "#0891b2" : "#8fa3c8", borderRadius: 8, fontSize: 14, cursor: "pointer",
          display: "flex", alignItems: "center", gap: 10, fontFamily: "'DM Sans',sans-serif",
        }}>
          <span style={{ opacity: 0.8, fontSize: 13 }}>{t.ic}</span>{t.l}
        </button>
      ))}
    </nav>

    {stats && (
      <div style={{ padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: "auto" }}>
        <p className="font-mono" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#4a6080", textTransform: "uppercase", marginBottom: 10 }}>PIPELINE SUMMARY</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
          <div><p style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>{stats.total}</p><p style={{ fontSize: 11, color: "#4a6080" }}>Total leads</p></div>
          <div><p style={{ fontSize: 22, fontWeight: 800, color: "#0891b2" }}>{stats.by_status?.new || 0}</p><p style={{ fontSize: 11, color: "#4a6080" }}>New</p></div>
        </div>
      </div>
    )}

    <div style={{ padding: 16, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
      <p style={{ fontSize: 12, color: "#8fa3c8" }}>{user?.name || user?.email}</p>
      <p className="font-mono" style={{ fontSize: 10, color: "#4a6080", letterSpacing: "0.1em", textTransform: "uppercase" }}>{user?.role}</p>
      <button data-testid={ADMIN.logout} onClick={onLogout} style={{ marginTop: 12, width: "100%", padding: "8px 12px", background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#8fa3c8", borderRadius: 8, fontSize: 12, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Sign out</button>
    </div>
  </aside>
);

// ---------- Dashboard ----------
const StatCard = ({ label, value, color }) => (
  <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: 20 }}>
    <p className="font-mono" style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.14em", textTransform: "uppercase" }}>{label}</p>
    <p style={{ fontSize: 30, fontWeight: 800, color: color || "#0c1a2e", letterSpacing: "-0.02em", marginTop: 4 }}>{value}</p>
  </div>
);

const Dashboard = ({ stats, onOpenLead }) => {
  if (!stats) return <p style={{ padding: 24, color: "#6b7280" }}>Loading…</p>;
  const maxDay = Math.max(1, ...stats.by_day.map((d) => d.count));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
        <StatCard label="Total leads" value={stats.total} color="#0c1a2e" />
        <StatCard label="New" value={stats.by_status.new || 0} color="#0891b2" />
        <StatCard label="Qualified" value={stats.by_status.qualified || 0} color="#d97706" />
        <StatCard label="Won" value={stats.by_status.won || 0} color="#16a34a" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 16 }}>
        <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: 24 }}>
          <p className="font-mono" style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>LEADS · LAST 14 DAYS</p>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#0c1a2e", marginBottom: 20 }}>Daily inbound</h3>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 160 }}>
            {stats.by_day.map((d) => (
              <div key={d.date} title={`${d.date}: ${d.count}`} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <div style={{ width: "100%", height: `${(d.count / maxDay) * 100}%`, background: d.count ? "#0891b2" : "#e2eaf2", borderRadius: "4px 4px 0 0", transition: "all 0.2s", minHeight: 3 }} />
                <span className="font-mono" style={{ fontSize: 9, color: "#94a3b8" }}>{d.date.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: 24 }}>
          <p className="font-mono" style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>BY SECTOR</p>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#0c1a2e", marginBottom: 16 }}>Sector distribution</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {Object.entries(stats.by_sector).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 13, color: "#374151", width: 100, textTransform: "capitalize" }}>{k || "unspecified"}</span>
                <div style={{ flex: 1, height: 8, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${(v / stats.total) * 100}%`, background: "#0891b2" }} />
                </div>
                <span className="font-mono" style={{ fontSize: 12, color: "#0c1a2e", fontWeight: 700, minWidth: 24, textAlign: "right" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: 24 }}>
        <p className="font-mono" style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 16 }}>RECENT LEADS</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {stats.recent.length === 0 && <p style={{ fontSize: 13, color: "#6b7280" }}>No leads yet. When someone submits the contact form, they&apos;ll appear here.</p>}
          {stats.recent.map((l) => (
            <button type="button" key={l.id} onClick={() => onOpenLead(l.id)} style={{ display: "grid", gridTemplateColumns: "1.5fr 2fr 1fr 1fr auto", gap: 12, padding: "12px 14px", background: "#f8fafc", border: "1px solid #e8edf4", borderRadius: 8, cursor: "pointer", alignItems: "center", width: "100%", textAlign: "left", fontFamily: "inherit", color: "inherit" }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#0c1a2e" }}>{l.name}</span>
              <span style={{ fontSize: 13, color: "#4a6070" }}>{l.email}</span>
              <span style={{ fontSize: 12, color: "#6b7280", textTransform: "capitalize" }}>{l.company || "—"}</span>
              <span style={{ fontSize: 11, color: statusColor(l.status), background: `${statusColor(l.status)}15`, border: `1px solid ${statusColor(l.status)}40`, borderRadius: 20, padding: "2px 10px", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", width: "fit-content" }}>{l.status}</span>
              <span className="font-mono" style={{ fontSize: 11, color: "#94a3b8" }}>{fmtDate(l.created_at)}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------- Leads list + detail ----------
const LeadsView = ({ onOpen }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sector, setSector] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (sector) params.sector = sector;
      const { data } = await api.get("/admin/leads", { params });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load leads"); }
    finally { setLoading(false); }
  }, [q, status, sector]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <input data-testid={ADMIN.search} placeholder="Search name, email, company…" value={q} onChange={(e) => setQ(e.target.value)}
          style={{ flex: 1, minWidth: 260, padding: "9px 12px", background: "#fff", border: "1px solid #e2eaf2", borderRadius: 8, fontSize: 14, color: "#0c1a2e", outline: "none", fontFamily: "'DM Sans',sans-serif" }} />
        <select data-testid={ADMIN.statusFilter} value={status} onChange={(e) => setStatus(e.target.value)} style={selectStyle}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <select data-testid={ADMIN.sectorFilter} value={sector} onChange={(e) => setSector(e.target.value)} style={selectStyle}>
          {SECTORS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
        </select>
        <span className="font-mono" style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.06em" }}>{total} results</span>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 2fr 1.2fr 1fr 1fr 1.1fr", gap: 0, padding: "12px 18px", background: "#f8fafc", borderBottom: "1px solid #e2eaf2" }}>
          {["Name", "Email", "Company", "Sector", "Status", "Received"].map((h) => (
            <span key={h} className="font-mono" style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}>{h}</span>
          ))}
        </div>
        {loading && <p style={{ padding: 18, color: "#6b7280", fontSize: 13 }}>Loading…</p>}
        {!loading && items.length === 0 && <p style={{ padding: 18, color: "#6b7280", fontSize: 13 }}>No leads match the current filters.</p>}
        {items.map((l) => (
          <button type="button" key={l.id} data-testid={ADMIN.leadRow(l.id)} onClick={() => onOpen(l.id)}
            style={{ display: "grid", gridTemplateColumns: "1.4fr 2fr 1.2fr 1fr 1fr 1.1fr", gap: 0, padding: "14px 18px", borderBottom: "1px solid #eef2f7", cursor: "pointer", alignItems: "center", width: "100%", background: "#fff", border: 0, borderBottomStyle: "solid", borderBottomWidth: 1, borderBottomColor: "#eef2f7", textAlign: "left", fontFamily: "inherit", color: "inherit" }}>
            <span data-testid={ADMIN.leadOpen(l.id)} style={{ fontSize: 14, fontWeight: 600, color: "#0c1a2e" }}>{l.name}</span>
            <span style={{ fontSize: 13, color: "#4a6070" }}>{l.email}</span>
            <span style={{ fontSize: 13, color: "#6b7280" }}>{l.company || "—"}</span>
            <span style={{ fontSize: 12, color: "#6b7280", textTransform: "capitalize" }}>{l.sector || "—"}</span>
            <span style={{ fontSize: 11, color: statusColor(l.status), background: `${statusColor(l.status)}15`, border: `1px solid ${statusColor(l.status)}40`, borderRadius: 20, padding: "2px 10px", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", width: "fit-content" }}>{l.status}</span>
            <span className="font-mono" style={{ fontSize: 11, color: "#94a3b8" }}>{fmtDate(l.created_at)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const selectStyle = { padding: "9px 12px", background: "#fff", border: "1px solid #e2eaf2", borderRadius: 8, fontSize: 13, color: "#0c1a2e", outline: "none", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" };

// ---------- Lead detail drawer ----------
const LeadDetail = ({ leadId, team, onClose, onChanged }) => {
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    try {
      const [{ data: l }, { data: n }] = await Promise.all([
        api.get(`/admin/leads/${leadId}`),
        api.get(`/admin/leads/${leadId}/notes`),
      ]);
      setLead(l); setNotes(n.items);
    } catch (e) { toast.error("Failed to load lead"); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const patch = async (updates) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/leads/${leadId}`, updates);
      setLead(data);
      onChanged && onChanged();
      toast.success("Updated");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    try {
      const { data } = await api.post(`/admin/leads/${leadId}/notes`, { body: noteBody.trim() });
      setNotes((prev) => [data, ...prev]);
      setNoteBody("");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Note failed"); }
  };

  const del = async () => {
    if (!window.confirm(`Delete lead ${lead.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/leads/${leadId}`);
      toast.success("Lead deleted");
      onChanged && onChanged();
      onClose();
    } catch (e) { toast.error("Delete failed"); }
  };

  if (!lead) return null;

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(12,26,46,0.5)", zIndex: 90 }} />
      <aside className="argov-drawer" style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 560, background: "#f2f5f8", zIndex: 91, overflow: "auto", boxShadow: "-8px 0 32px rgba(0,0,0,0.15)" }}>
        <div style={{ padding: 24, borderBottom: "1px solid #e2eaf2", background: "#fff", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <p className="font-mono" style={{ fontSize: 10, color: "#0891b2", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>LEAD · {lead.id.slice(0, 8)}</p>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0c1a2e", letterSpacing: "-0.02em" }}>{lead.name}</h2>
            <p style={{ fontSize: 13, color: "#4a6070", marginTop: 4 }}>{lead.email}</p>
            {lead.company && <p style={{ fontSize: 13, color: "#4a6070" }}>{lead.company}{lead.role ? ` · ${lead.role}` : ""}</p>}
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 24, fontWeight: 300, padding: 4 }}>×</button>
        </div>

        <div style={{ padding: 24, display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Pipeline controls */}
          <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: 20 }}>
            <p className="font-mono" style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14 }}>PIPELINE</p>
            <label style={fieldLabel}>Status</label>
            <select data-testid={ADMIN.detailStatus} value={lead.status} disabled={busy} onChange={(e) => patch({ status: e.target.value })} style={selectStyle}>
              {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <label style={{ ...fieldLabel, marginTop: 14 }}>Priority</label>
            <select data-testid={ADMIN.detailPriority} value={lead.priority} disabled={busy} onChange={(e) => patch({ priority: e.target.value })} style={selectStyle}>
              {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
            </select>
            <label style={{ ...fieldLabel, marginTop: 14 }}>Assigned to</label>
            <select data-testid={ADMIN.detailAssignee} value={lead.assigned_to || ""} disabled={busy} onChange={(e) => patch({ assigned_to: e.target.value })} style={selectStyle}>
              <option value="">Unassigned</option>
              {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} · {u.role}</option>)}
            </select>
          </div>

          {/* Details */}
          <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: 20 }}>
            <p className="font-mono" style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 12 }}>DETAILS</p>
            <KV label="Sector" value={lead.sector || "—"} />
            <KV label="Source" value={lead.source} />
            <KV label="Received" value={fmtDate(lead.created_at)} />
            <KV label="IP" value={lead.ip || "—"} mono />
            {lead.environment && (
              <>
                <p style={fieldLabel}>Environment</p>
                <p style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, whiteSpace: "pre-wrap", padding: "10px 12px", background: "#f8fafc", border: "1px solid #e8edf4", borderRadius: 8 }}>{lead.environment}</p>
              </>
            )}
          </div>

          {/* Notes */}
          <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: 20 }}>
            <p className="font-mono" style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 12 }}>NOTES &amp; ACTIVITY</p>
            <textarea data-testid={ADMIN.noteInput} placeholder="Add a note (activity log entry)…" rows={3} value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", border: "1px solid #e2eaf2", borderRadius: 8, fontSize: 14, background: "#f8fafc", outline: "none", resize: "vertical", fontFamily: "'DM Sans',sans-serif" }} />
            <button data-testid={ADMIN.noteSubmit} onClick={addNote} disabled={!noteBody.trim()} style={{ marginTop: 10, padding: "8px 18px", background: noteBody.trim() ? "#0891b2" : "#cbd5e1", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: noteBody.trim() ? "pointer" : "not-allowed" }}>Add note</button>
            <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              {notes.map((n) => (
                <div key={n.id} style={{ background: "#f8fafc", border: "1px solid #e8edf4", borderRadius: 8, padding: 12 }}>
                  <p style={{ fontSize: 13, color: "#0c1a2e", lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{n.body}</p>
                  <p className="font-mono" style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.06em", marginTop: 8 }}>{n.author_name} · {fmtDate(n.created_at)}</p>
                </div>
              ))}
              {notes.length === 0 && <p style={{ fontSize: 12, color: "#94a3b8" }}>No notes yet.</p>}
            </div>
          </div>

          <button data-testid={ADMIN.detailDelete} onClick={del} style={{ padding: "10px 16px", background: "transparent", border: "1px solid rgba(220,38,38,0.3)", color: "#dc2626", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Delete lead</button>
        </div>
      </aside>
    </>
  );
};

const fieldLabel = { display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 6, letterSpacing: "0.02em" };

const KV = ({ label, value, mono }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #f1f5f9", fontSize: 13 }}>
    <span style={{ color: "#6b7280" }}>{label}</span>
    <span className={mono ? "font-mono" : ""} style={{ color: "#0c1a2e", fontWeight: 500, fontSize: mono ? 12 : 13 }}>{value}</span>
  </div>
);

// ---------- Export view ----------
const ExportView = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/leads/export/csv`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("argov_token")}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `argov-leads-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export ready");
    } catch (e) { toast.error("Export failed"); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ background: "#fff", border: "1px solid #e2eaf2", borderRadius: 12, padding: 32, maxWidth: 640 }}>
      <p className="font-mono" style={{ fontSize: 10, color: "#94a3b8", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>DATA EXPORT</p>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0c1a2e", marginBottom: 8 }}>Export leads as CSV</h2>
      <p style={{ fontSize: 14, color: "#4a6070", lineHeight: 1.65, marginBottom: 20 }}>
        Download a full CSV of every lead in the CRM — ready for import into your GRC or SIEM. Personal data is included, so store the file in a governed location.
      </p>
      <button data-testid={ADMIN.exportCsv} onClick={download} disabled={busy}
        style={{ padding: "11px 22px", background: "#0891b2", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: busy ? "wait" : "pointer" }}>
        {busy ? "Preparing…" : "Download leads.csv →"}
      </button>
    </div>
  );
};

// ---------- Page ----------
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState([]);
  const [openLead, setOpenLead] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const loadCommon = useCallback(async () => {
    try {
      const [{ data: s }, { data: t }] = await Promise.all([
        api.get("/admin/analytics"), api.get("/admin/team"),
      ]);
      setStats(s); setTeam(t.items);
    } catch (e) { toast.error("Failed to load"); }
  }, []);

  useEffect(() => { loadCommon(); }, [loadCommon, refreshTick]);

  const onLogout = async () => { await logout(); nav("/", { replace: true }); };

  return (
    <div data-testid={ADMIN.root} className="argov-admin-shell" style={{ display: "flex", minHeight: "100vh", background: "#f2f5f8" }}>
      <Toaster position="top-right" richColors />
      <div className="argov-admin-sidebar" style={{ display: "contents" }}>
        <Sidebar tab={tab} setTab={setTab} onLogout={onLogout} user={user} stats={stats} />
      </div>
      <div style={{ flex: 1, padding: "28px 32px", minWidth: 0 }}>
        <header style={{ marginBottom: 24 }}>
          <p className="font-mono" style={{ fontSize: 11, letterSpacing: "0.14em", color: "#0891b2", textTransform: "uppercase" }}>{tab.toUpperCase()}</p>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "#0c1a2e", letterSpacing: "-0.02em" }}>
            {tab === "dashboard" ? "Pipeline overview" : tab === "leads" ? "All leads" : "Export leads"}
          </h1>
        </header>
        {tab === "dashboard" && <Dashboard stats={stats} onOpenLead={setOpenLead} />}
        {tab === "leads" && <LeadsView onOpen={setOpenLead} />}
        {tab === "export" && <ExportView />}
      </div>
      {openLead && (
        <LeadDetail leadId={openLead} team={team} onClose={() => setOpenLead(null)} onChanged={() => setRefreshTick((n) => n + 1)} />
      )}
    </div>
  );
}
